<script lang="ts">
import type { PropType, CreateElement, VNode } from "vue";
import { computed, defineComponent } from "vue";
import { VCol, VContainer, VRow } from "vuetify/lib";

function isEmptyNode(node: VNode): boolean {
  if (node.isComment) {
    return true;
  } else if (node.children !== undefined) {
    return !node.children.some(isEmptyNode);
  } else if (
    node.tag === undefined &&
    (!node.text?.length || !node.text?.trim())
  ) {
    return true;
  } else if (node.componentOptions?.tag === "transition") {
    // If this is a transition element - check to make sure we have visible children.

    // This does not seem to preserve the transition behaviour despite emitting
    // the transition object unwrapped. Still, this is better than leaving a big
    // gap.
    return node.componentOptions.children?.every(isEmptyNode) ?? true;
  }

  return false;
}

export type FlowLayoutSize = keyof typeof flowLayoutSizes;

export const flowLayoutSizes = {
  "full-width": {
    cols: 12,
    sm: 6,
    md: 4,
    lg: 3,
    xl: 2,
  },
  "half-width": {
    cols: 12,
    md: 6,
    xl: 4,
  },
  "single-width": {
    cols: 12,
  },
};

/**
 * Full Width - For when the parent component takes the full width of the screen
 * <bm-form-flow-layout cols="12" sm="6" md="4" lg="3" xl="2">
 *
 * Half Width - For when the parent component takes half the width of the screen
 * <bm-form-flow-layout cols="12" md="6" xl="4">
 */

export default defineComponent({
  name: "BmFormFlowLayout",
  props: {
    cols: {
      type: [Boolean, String, Number],
      default: false,
    },
    sm: {
      type: [Boolean, String, Number],
      default: false,
    },
    md: {
      type: [Boolean, String, Number],
      default: false,
    },
    lg: {
      type: [Boolean, String, Number],
      default: false,
    },
    xl: {
      type: [Boolean, String, Number],
      default: false,
    },
    column: {
      type: Boolean,
      required: false,
    },
    rowAlign: {
      type: String as PropType<"start" | "center" | "end">,
      required: false,
      default: "start",
    },
    rowDense: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, context) {
    const colProps = computed(() => {
      return {
        cols: props.column ? "12" : props.cols,
        sm: props.sm,
        md: props.md,
        lg: props.lg,
        xl: props.xl,
      };
    });
    return { colProps };
  },
  render(h: CreateElement): VNode {
    const children = (this.$slots.default as VNode[]) ?? [];

    const wrappedChildren: VNode[] = [];

    children.forEach((child) => {
      if (isEmptyNode(child)) {
        wrappedChildren.push(child);
      } else {
        let wrappedChild = h(VCol, { props: this.colProps }, [child]);
        wrappedChildren.push(wrappedChild);
      }
    });
    const rows = [
      h(VRow, { props: { align: this.rowAlign, dense: this.rowDense } }, [
        wrappedChildren,
      ]),
    ];
    return h(VContainer, { class: "pa-0", props: { fluid: true } }, rows);
  },
});
</script>
